/**
 * Formula Engine for Salary Structure Calculations
 * Handles formula parsing, validation, and calculation
 */
import { isBasicLike, isHraLike } from "./salaryStructureSimulatorCompute";

const escapeRegex = (s) => String(s).replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

/** Exact component name not glued to other identifier characters (no lookbehind: older iOS Safari). */
const nameRegex = (name) =>
  new RegExp(`(^|[^A-Za-z0-9_])${escapeRegex(name)}(?=[^A-Za-z0-9_]|$)`, "g");

/** Replace known component names (longest first) with 0 so spaces inside names don't split them. */
function maskNames(formula, names) {
  return [...new Set((names || []).filter(Boolean).map(String))]
    .sort((a, b) => b.length - a.length)
    .reduce((text, name) => text.replace(nameRegex(name), (_, lead) => `${lead}0`), String(formula || ""));
}

/** Replace whole terms (case-insensitive, longest first) using map { term: replacement }. */
function replaceTerms(formula, map) {
  const keys = Object.keys(map).filter(Boolean).sort((a, b) => b.length - a.length);
  const text = String(formula || "");
  if (!keys.length) return text;
  const lookup = new Map(keys.map((k) => [k.toLowerCase(), map[k]]));
  const re = new RegExp(`(^|[^A-Za-z0-9_])(${keys.map(escapeRegex).join("|")})(?=[^A-Za-z0-9_]|$)`, "gi");
  return text.replace(re, (_, lead, term) => `${lead}${lookup.get(term.toLowerCase())}`);
}

const normName = (s) => String(s || "").trim().replace(/\s+/g, " ").toLowerCase();
const compCode = (c) => String(c?.code || "").trim();
const hasTerm = (text, term) => new RegExp(nameRegex(term).source, "i").test(String(text || ""));

/**
 * Display formula (component names) → stored formula (component codes).
 * Legacy BASIC / HRA words map to this company's Basic / HRA component code.
 */
export function formulaToCodes(formula, components = []) {
  const map = {};
  const withCode = (components || []).filter((c) => compCode(c));
  const known = new Set();
  withCode.forEach((c) => {
    known.add(normName(c.name));
    known.add(compCode(c).toLowerCase());
  });
  [
    ["BASIC", withCode.find(isBasicLike)],
    ["HRA", withCode.find(isHraLike)],
  ].forEach(([alias, comp]) => {
    if (comp && !known.has(alias.toLowerCase())) map[alias] = compCode(comp);
  });
  withCode.forEach((c) => {
    map[compCode(c)] = compCode(c);
    if (c.name) map[String(c.name).trim()] = compCode(c);
  });
  return replaceTerms(formula, map);
}

/** Stored formula (component codes) → display formula (component names). */
export function formulaToNames(formula, components = []) {
  const map = {};
  (components || []).forEach((c) => {
    if (compCode(c) && c.name) map[compCode(c)] = String(c.name).trim();
  });
  return replaceTerms(formula, map);
}

/** Components (with a code) referenced by a stored formula. */
export function formulaComponents(codeFormula, components = []) {
  return (components || []).filter((c) => compCode(c) && hasTerm(codeFormula, compCode(c)));
}

/** Names used in the display formula that belong to more than one component of this company. */
export function duplicateNamesUsed(formula, components = []) {
  const counts = new Map();
  (components || []).forEach((c) => {
    const n = normName(c?.name);
    if (n) counts.set(n, { name: String(c.name).trim(), count: (counts.get(n)?.count || 0) + 1 });
  });
  return [...counts.values()]
    .filter(({ name, count }) => count > 1 && hasTerm(formula, name))
    .map(({ name }) => name);
}

/**
 * Stored formula must only use components calculated before `target` in `structureComponents`.
 * Returns error strings (empty = OK). Order follows backend: orderNo || sortOrder || 0.
 */
export function formulaOrderErrors(codeFormula, target, structureComponents = [], components = []) {
  const order = (c) => Number(c?.orderNo || c?.sortOrder || 0);
  const targetCode = compCode(target).toUpperCase();
  return formulaComponents(codeFormula, components).flatMap((ref) => {
    const code = compCode(ref).toUpperCase();
    const label = ref.name || code;
    if (code === targetCode) return [`${target.name || code} cannot use itself in its formula`];
    const inStructure = (structureComponents || []).find((c) => compCode(c).toUpperCase() === code);
    if (!inStructure) return [`${label} is not part of this structure`];
    if (order(inStructure) >= order(target)) {
      return [`${label} (order ${order(inStructure)}) must come before ${target.name || targetCode} (order ${order(target)})`];
    }
    return [];
  });
}

class FormulaEngine {
  constructor() {
    this.functions = {
      SUM: (...args) => args.reduce((sum, val) => sum + (Number(val) || 0), 0),
      AVG: (...args) => {
        const validArgs = args.filter(val => !isNaN(Number(val)));
        return validArgs.length > 0 ? validArgs.reduce((sum, val) => sum + Number(val), 0) / validArgs.length : 0;
      },
      MAX: (...args) => Math.max(...args.map(val => Number(val) || 0)),
      MIN: (...args) => Math.min(...args.map(val => Number(val) || 0)),
      ROUND: (value, decimals = 0) => Math.round(Number(value) * Math.pow(10, decimals)) / Math.pow(10, decimals),
      CEIL: (value) => Math.ceil(Number(value) || 0),
      FLOOR: (value) => Math.floor(Number(value) || 0),
      ABS: (value) => Math.abs(Number(value) || 0),
      IF: (condition, trueValue, falseValue) => condition ? trueValue : falseValue,
      AND: (...args) => args.every(arg => Boolean(arg)),
      OR: (...args) => args.some(arg => Boolean(arg)),
      NOT: (value) => !value
    };

    this.operators = {
      '+': (a, b) => Number(a) + Number(b),
      '-': (a, b) => Number(a) - Number(b),
      '*': (a, b) => Number(a) * Number(b),
      '/': (a, b) => Number(b) !== 0 ? Number(a) / Number(b) : 0,
      '%': (a, b) => Number(b) !== 0 ? Number(a) % Number(b) : 0,
      '^': (a, b) => Math.pow(Number(a), Number(b)),
      '==': (a, b) => Number(a) === Number(b),
      '!=': (a, b) => Number(a) !== Number(b),
      '>': (a, b) => Number(a) > Number(b),
      '<': (a, b) => Number(a) < Number(b),
      '>=': (a, b) => Number(a) >= Number(b),
      '<=': (a, b) => Number(a) <= Number(b),
      '&&': (a, b) => Boolean(a) && Boolean(b),
      '||': (a, b) => Boolean(a) || Boolean(b)
    };
  }

  /**
   * Validate formula syntax
   * @param {string} formula
   * @param {Array} availableComponents - e.g. [{ id, name }] including CTC base component
   * @param {Object} statutoryRates
   */
  validateFormula(formula, availableComponents = [], statutoryRates = {}) {
    const errors = [];
    
    if (!formula || typeof formula !== 'string') {
      errors.push('Formula must be a non-empty string');
      return { isValid: false, errors };
    }

    // Check for balanced parentheses
    let openCount = 0;
    for (let char of formula) {
      if (char === '(') openCount++;
      if (char === ')') openCount--;
      if (openCount < 0) {
        errors.push('Unbalanced parentheses');
        break;
      }
    }
    if (openCount > 0) {
      errors.push('Unbalanced parentheses');
    }

    // Check for valid function names and variables (use availableComponents so CTC etc. are valid)
    const functionNames = Object.keys(this.functions);
    const componentNames = (availableComponents || []).flatMap((c) => [c?.name, c?.code]);
    const usedFunctions =
      maskNames(formula, componentNames).match(/\b[A-Za-z_][A-Za-z0-9_]*\b/g) || [];
    const invalidFunctions = usedFunctions.filter(func => 
      !functionNames.includes(func) && 
      !this.isValidVariable(func, availableComponents, statutoryRates)
    );
    
    if (invalidFunctions.length > 0) {
      errors.push(`Invalid function or variable: ${invalidFunctions.join(', ')}`);
    }

    // Check for valid operators
    const validOperators = Object.keys(this.operators);
    const formulaOperators = formula.match(/[+\-*/%^=!><&|]+/g) || [];
    const invalidOperators = formulaOperators.filter(op => !validOperators.includes(op));
    
    if (invalidOperators.length > 0) {
      errors.push(`Invalid operator: ${invalidOperators.join(', ')}`);
    }

    return { isValid: errors.length === 0, errors };
  }

  /**
   * Extract variables from formula
   */
  extractVariables(formula, availableComponents = [], statutoryRates = {}) {
    const variables = [];
    const componentNames = availableComponents.map(comp => comp.name);
    const statutoryNames = Object.keys(statutoryRates);
    
    // Multi-word component names (e.g. "Basic Salary") first, then single words
    componentNames.forEach((name) => {
      if (name && /\s/.test(name) && nameRegex(name).test(formula) && !variables.includes(name)) {
        variables.push(name);
      }
    });
    const words = maskNames(formula, componentNames.filter((n) => /\s/.test(n || ""))).match(/\b[A-Za-z_][A-Za-z0-9_]*\b/g) || [];
    
    words.forEach(word => {
      if (!Object.keys(this.functions).includes(word) && 
          (componentNames.includes(word) || statutoryNames.includes(word))) {
        if (!variables.includes(word)) {
          variables.push(word);
        }
      }
    });
    
    return variables;
  }

  /**
   * Check if a string is a valid variable name
   */
  isValidVariable(variable, availableComponents = [], statutoryRates = {}) {
    const v = String(variable || "");
    if (!v) return false;
    const upper = v.toUpperCase();
    // Company components come in via availableComponents; no fixed BASIC / HRA / GROSS here
    const builtin = new Set([
      "CTC",
      "MONTHLY_CTC",
      "PF_RATE",
      "ESI_RATE",
      "GRATUITY_RATE",
      "TDS_THRESHOLD",
      "MAX_PF_LIMIT",
    ]);
    if (builtin.has(upper) || builtin.has(v)) return true;

    const componentMatch = (availableComponents || []).some((comp) => {
      const name = String(comp?.name || "");
      const code = String(comp?.code || "");
      return name === v || code === v || code.toUpperCase() === upper;
    });
    if (componentMatch) return true;

    const statutoryNames = Object.keys(statutoryRates || {});
    if (statutoryNames.includes(v) || statutoryNames.includes(upper)) return true;

    // Flat statutory aliases sometimes nested under pf/esi keys
    if (["pf", "esi", "gratuity", "pt"].includes(v.toLowerCase())) return true;

    return false;
  }

  /**
   * Calculate formula result
   */
  calculateFormula(formula, variables = {}, context = {}) {
    try {
      // Merge context into variables; CTC always preferred from variables (monthly sim)
      const vars = { ...context, ...variables };

      // Excel-style: CTC*50 or CTC*50% → 50% of CTC (monthly)
      let processedFormula = String(formula || "");
      const bases = "CTC|MONTHLY_CTC|ANNUAL_CTC|BASIC|GROSS|HRA|Basic";
      processedFormula = processedFormula.replace(
        new RegExp(`\\b(${bases})\\s*\\*\\s*(\\d{1,3})\\s*%`, "gi"),
        (_, base, num) => `((${base})*${Number(num)}/100)`
      );
      processedFormula = processedFormula.replace(
        new RegExp(`\\b(\\d{1,3})\\s*%\\s*\\*\\s*(${bases})\\b`, "gi"),
        (_, num, base) => `((${base})*${Number(num)}/100)`
      );
      processedFormula = processedFormula.replace(
        new RegExp(`\\b(${bases})\\s*\\*\\s*(\\d{1,3})(?!\\s*[/\\.])`, "gi"),
        (match, base, num, offset, full) => {
          const n = Number(num);
          if (n < 1 || n > 100) return match;
          const after = full.slice(offset + match.length);
          if (/^\s*\/\s*100\b/.test(after) || /^\s*%/.test(after)) return match;
          return `((${base})*${n}/100)`;
        }
      );
      processedFormula = processedFormula.replace(
        new RegExp(`\\b(\\d{1,3})\\s*\\*\\s*(${bases})\\b`, "gi"),
        (match, num, base) => {
          const n = Number(num);
          if (n < 1 || n > 100) return match;
          return `((${base})*${n}/100)`;
        }
      );
      // Any remaining "40%" means 40/100 (same as backend evalTemplateFormula)
      processedFormula = processedFormula.replace(
        /(^|[^A-Za-z0-9_.])(\d+(?:\.\d+)?)\s*%/g,
        (_, lead, num) => `${lead}(${num}/100)`
      );

      // Replace longer names first so BASIC is not partially replaced inside names
      const keys = Object.keys(vars).sort((a, b) => b.length - a.length);
      keys.forEach((variable) => {
        const value = vars[variable];
        if (value == null || typeof value === "object") return;
        const regex = new RegExp(`\\b${variable.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")}\\b`, "g");
        processedFormula = processedFormula.replace(regex, String(Number(value) || 0));
      });

      processedFormula = this.processFunctions(processedFormula);
      const result = this.evaluateExpression(processedFormula);

      return {
        success: true,
        result: Number(result) || 0,
        processedFormula,
      };
    } catch (error) {
      return {
        success: false,
        error: error.message,
        result: null,
      };
    }
  }

  /**
   * Process function calls in formula
   */
  processFunctions(formula) {
    let text = String(formula || "");
    const callRegex = new RegExp(`\\b(${Object.keys(this.functions).join("|")})\\s*\\(`, "g");

    // Innermost call = last call start; resolve it, then repeat (handles nested brackets)
    for (;;) {
      const calls = [...text.matchAll(callRegex)];
      if (!calls.length) return text;
      const call = calls[calls.length - 1];
      const open = call.index + call[0].length - 1;

      let depth = 0;
      let close = -1;
      const commas = [];
      for (let i = open; i < text.length; i++) {
        if (text[i] === "(") depth++;
        else if (text[i] === ")" && --depth === 0) {
          close = i;
          break;
        } else if (text[i] === "," && depth === 1) commas.push(i);
      }
      if (close < 0) return text;

      const bounds = [open, ...commas, close];
      const args = bounds.slice(0, -1).map((start, i) => {
        const arg = text.slice(start + 1, bounds[i + 1]).trim();
        if (!/^[0-9+\-*/().\s]+$/.test(arg)) return arg;
        try {
          return this.evaluateExpression(arg);
        } catch {
          return arg;
        }
      });

      let result;
      try {
        result = this.functions[call[1]](...args);
      } catch {
        return text;
      }
      const out = typeof result === "number" && result < 0 ? `(${result})` : String(result);
      text = text.slice(0, call.index) + out + text.slice(close + 1);
    }
  }

  /**
   * Evaluate mathematical expression safely
   */
  evaluateExpression(expression) {
    // Remove any remaining non-mathematical characters
    const cleanExpression = expression.replace(/[^0-9+\-*/().]/g, '');
    
    // Use Function constructor for safer evaluation than eval
    try {
      return new Function('return ' + cleanExpression)();
    } catch (error) {
      throw new Error('Invalid mathematical expression');
    }
  }

  /**
   * Generate mock data for testing formulas
   */
  generateMockData(components = [], statutoryRates = {}) {
    const mockData = {};
    
    // Generate mock component values
    components.forEach(component => {
      mockData[component.name] = Math.floor(Math.random() * 20000) + 5000; // 5000-25000 range
    });
    
    // Add statutory rates
    Object.keys(statutoryRates).forEach(rate => {
      mockData[rate] = statutoryRates[rate];
    });
    
    return mockData;
  }

  /**
   * Calculate salary structure totals
   */
  calculateStructureTotals(components, formulas = {}) {
    const totals = {
      earnings: 0,
      deductions: 0,
      benefits: 0,
      reimbursements: 0,
      gross: 0,
      net: 0
    };

    components.forEach(component => {
      let amount = 0;
      
      if (component.calculationType === 'formula' && component.selectedFormula) {
        // Calculate using formula
        const mockData = this.generateMockData(components);
        const calculation = this.calculateFormula(
          component.selectedFormula.formula,
          mockData
        );
        amount = calculation.success ? calculation.result : 0;
      } else {
        // Manual calculation (use formulaResult if available)
        amount = component.formulaResult || 0;
      }

      // Categorize amounts
      if (component.categories.earning) totals.earnings += amount;
      if (component.categories.deduction) totals.deductions += amount;
      if (component.categories.benefit) totals.benefits += amount;
      if (component.categories.reimbursement) totals.reimbursements += amount;
    });

    totals.gross = totals.earnings + totals.benefits + totals.reimbursements;
    totals.net = totals.gross - totals.deductions;

    return totals;
  }
}

// Create singleton instance
const formulaEngine = new FormulaEngine();

export default formulaEngine;
